import { useState } from 'react'

import { Button, Card, Section } from '@factorialco/deprecated-design-system'
import { F0Form, f0FormField } from '@factorialco/f0-react'
import { z } from 'zod'

import { observer } from 'mobx-react'

import Modal from 'modules/core/components/Modal'
import useAsyncAction from 'modules/core/hooks/useAsyncAction'
import i18n from 'modules/core/lib/i18n'

import type GDPRDataRetention from 'modules/ats/store/models/GDPRDataRetention'

interface Props {
  currentRetention: GDPRDataRetention
}

const DataErasure = ({ currentRetention }: Props) => {
  const schema = z.object({
    grace_period_before_deletion: f0FormField(z.coerce.number().int().nonnegative(), {
      label: `${i18n.t(
        'ats.settings.data_erasure.candidates_with_expired_permission.inputs_label.delete_after'
      )} (${i18n.t('ats.settings.data_erasure.candidates_with_expired_permission.days').toLocaleLowerCase(i18n.locale)})`,
    }),
  })

  const { performAction: saveToggleData } = useAsyncAction(async (value: boolean) => {
    await currentRetention?.save({ auto_deletion_enabled: value })
  })

  const active = !!currentRetention?.attributes.auto_deletion_enabled
  const [confirmationModal, toggleConfirmationModal] = useState(false)

  const toggle = (isExpanded: boolean) => {
    if (isExpanded) {
      toggleConfirmationModal(true)
    } else {
      saveToggleData(false)
    }
  }

  const handleSubmit = async (values: z.infer<typeof schema>) => {
    const updated = await currentRetention?.save(values)
    return { success: !!updated }
  }

  return (
    <Section.TwoThirds
      description={i18n.t('app_store_v2.apps.ats.settings.data_erasure.description')}
      illustration="delete"
      title={i18n.t('app_store_v2.apps.ats.settings.data_erasure.title')}
    >
      <Card>
        <Card.Title size="small">
          {i18n.t('ats.settings.data_erasure.candidates_with_expired_permission.title')}
        </Card.Title>
        <Card.Description>
          {i18n.t('ats.settings.data_erasure.candidates_with_expired_permission.description')}
        </Card.Description>
        <Card.ExpandableContent
          expanded={active}
          onExpandChange={toggle}
          disabled={!currentRetention.attributes.consent_limit_enabled}
        >
          <F0Form
            name="data-erasure-grace-period"
            schema={schema}
            defaultValues={{
              grace_period_before_deletion:
                currentRetention.attributes.grace_period_before_deletion,
            }}
            onSubmit={handleSubmit}
            submitConfig={{ type: 'default', label: i18n.t('common.submit') }}
          />
        </Card.ExpandableContent>
      </Card>
      {confirmationModal && (
        <Modal
          title={i18n.t('ats.settings.data_erasure.modal.title')}
          description={i18n.t('ats.settings.data_erasure.modal.description')}
          illustration="alert"
          onClose={() => toggleConfirmationModal(false)}
          onSubmit={() => {
            toggleConfirmationModal(false)
            saveToggleData(true)
          }}
          footer={
            <Button type="negative" submit>
              {i18n.t('ats.settings.data_erasure.modal.submit_button')}
            </Button>
          }
        />
      )}
    </Section.TwoThirds>
  )
}

export default observer(DataErasure)
